import { Box } from '@mui/system';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections';
import type { GeographicTargetingState } from '@openx/types/targeting/geographic';
import { GeographicOption } from '@openx/types/targeting/geographic';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import { Fragment, type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParameterRemove } from '../../../utils/hooks/useParameterRemove';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine';
import {
  TargetingDropdownSection,
  type TargetingDropdownSectionProps,
} from '../../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection';
import { LocationSourceItems } from '../GeographicLocationItems/LocationSourceItems';

type GeographicItemsWithLocationSourceProps = {
  geographicOptions: [string, (() => JSX.Element | null) | null][];
  optionsNames: Record<string, string>;
  targetingParams: GeographicTargetingState;
  selectedInventories: string[];
  onRemoveParameter?: (option: GeographicOption) => void;
} & TargetingDropdownSectionProps;

export const GeographicItemsWithLocationSource = ({
  geographicOptions,
  optionsNames,
  targetingParams,
  selectedInventories,
  hideButtons,
  isDisabled,
  onOpenDrawerClick,
  onRemove,
  onRemoveParameter,
  optionsCount,
  disabledReasonMessage,
}: GeographicItemsWithLocationSourceProps) => {
  const { t } = useTranslation();

  const parameterRemoveHandler = useParameterRemove<GeographicOption>(onRemoveParameter, onRemove);

  const optionsWithoutSource = useMemo(
    () => geographicOptions.filter(([key]) => key !== GeographicOption.SOURCE),
    [geographicOptions]
  );

  const configuredKeys = useMemo(
    () => optionsWithoutSource.filter(([, component]) => Boolean(component)).map(([key]) => key),
    [optionsWithoutSource]
  );

  const hasNextConfigured = (key: string) => {
    const idx = configuredKeys.indexOf(key);
    return idx !== -1 && idx < configuredKeys.length - 1;
  };

  return (
    <>
      <IndentedSections sx={{ mt: 3 }}>
        {optionsWithoutSource.map(([key, component], index) => {
          return (
            <Fragment key={key}>
              <TargetingDropdownSection
                name={t(optionsNames[key])}
                hideButtons={hideButtons}
                isDisabled={isDisabled}
                disabledReasonMessage={disabledReasonMessage}
                isEmpty={!component}
                onOpenDrawerClick={() => onOpenDrawerClick?.(key)}
                onRemove={parameterRemoveHandler(key as GeographicOption)}
                sx={{ pt: '0 !important' }}
                hasOptionsSelected={!!optionsCount && optionsCount > 0}
              >
                <Box key={`k-${index}`}>{component ? component() : null}</Box>
              </TargetingDropdownSection>

              {index < optionsWithoutSource.length - 1 && (
                <DimensionChipLine
                  targetingDimension={Criteria.ANY}
                  showLabel={Boolean(component) && hasNextConfigured(key)}
                />
              )}
            </Fragment>
          );
        })}
      </IndentedSections>

      <DimensionChipLine targetingDimension={Criteria.ALL} showLabel={Boolean(selectedInventories.length > 1)} />

      <TargetingDropdownSection
        name={t(optionsNames[GeographicOption.SOURCE])}
        hideButtons={hideButtons}
        isDisabled={isDisabled}
        disabledReasonMessage={disabledReasonMessage}
        isEmpty={false}
        onOpenDrawerClick={() => onOpenDrawerClick?.(GeographicOption.SOURCE)}
        onRemove={parameterRemoveHandler(GeographicOption.SOURCE)}
        hasOptionsSelected={!!optionsCount && optionsCount > 0}
      >
        <LocationSourceItems
          targetingParams={targetingParams}
          readonly={true}
          onChange={noop}
          disabledReasonMessage={disabledReasonMessage}
        />
      </TargetingDropdownSection>
    </>
  );
};
